import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import AdminService from '../services/AdminService';
import toast from 'react-hot-toast';
import soundEffects from '../utils/soundEffects';
import { createRipple } from '../utils/vfx';
import {
  FaBook,
  FaEdit,
  FaSave,
  FaTimes,
  FaSearch,
  FaImage,
  FaTrash,
  FaPlus,
  FaFilter,
  FaClipboardCheck,
  FaFilePdf,
  FaMagic,
  FaSyncAlt
} from 'react-icons/fa';
import RichTextEditor from './RichTextEditor';
import PdfLessonUploader from './PdfLessonUploader';
import { markdownToHtml } from '../utils/markdownToHtml';
import { parseWorkshopDoc } from '../utils/workshopFormat';
import './AdminLessonsView.css';

const AdminLessonsView = ({ adminLevel, hasAdminLevel }) => {
  const [lessons, setLessons] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedUnit, setSelectedUnit] = useState('');
  const [selectedLesson, setSelectedLesson] = useState('');
  const [editingLesson, setEditingLesson] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    content: '',
    order: 0,
    xpValue: 50,
    isActive: true
  });
  
  // Create modals state
  const [showCreateCourse, setShowCreateCourse] = useState(false);
  const [showCreateUnit, setShowCreateUnit] = useState(false);
  const [showCreateLesson, setShowCreateLesson] = useState(false);
  const [showCreateQuiz, setShowCreateQuiz] = useState(null); // lesson ID
  const [showPdfUploader, setShowPdfUploader] = useState(false);
  const [selectedUnitForPdf, setSelectedUnitForPdf] = useState(null);
  const [improvingLesson, setImprovingLesson] = useState(false);
  // interactive workshop lessons store JSON — edit raw, never via RichTextEditor
  const [editingWorkshop, setEditingWorkshop] = useState(false);
  
  // Create forms state
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    thumbnail: '',
    color: '',
    order: '',
    isLocked: false
  });
  
  const [unitForm, setUnitForm] = useState({
    courseId: '',
    title: '',
    description: '',
    order: '',
    isActive: true
  });
  
  const [lessonForm, setLessonForm] = useState({
    unitId: '',
    title: '',
    content: '',
    order: '',
    xpValue: 50,
    isActive: true
  });
  
  const [quizForm, setQuizForm] = useState({
    lessonId: '',
    title: '',
    description: '',
    passingScore: 70,
    xpValue: 25,
    timeLimit: '',
    isActive: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [lessonsData, coursesData] = await Promise.all([
        AdminService.getLessons(),
        AdminService.getCoursesAndUnits()
      ]);
      setLessons(lessonsData.lessons || []);
      setCourses(coursesData.courses || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load lessons');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (lesson) => {
    soundEffects.playClick();
    setEditingLesson(lesson.id);
    const content = lesson.content || '';
    // interactive workshop lessons are JSON — running them through the rich text
    // editor would destroy the format, so edit them as raw text instead
    const isWorkshop = !!parseWorkshopDoc(content);
    setEditingWorkshop(isWorkshop);
    setEditForm({
      title: lesson.title,
      content: isWorkshop ? content : markdownToHtml(content),
      order: lesson.order,
      xpValue: lesson.xpValue,
      isActive: lesson.isActive
    });
  };

  const handleCancelEdit = () => {
    soundEffects.playClick();
    setEditingLesson(null);
    setEditingWorkshop(false);
    setEditForm({
      title: '',
      content: '',
      order: 0,
      xpValue: 50,
      isActive: true
    });
  };

  const handleSave = async () => {
    try {
      soundEffects.playClick();
      // Don't let a broken edit corrupt an interactive workshop
      if (editingWorkshop && !parseWorkshopDoc(editForm.content)) {
        toast.error('Invalid Workshop JSON — fix the format before saving');
        return;
      }
      await AdminService.updateLesson(editingLesson, editForm);
      toast.success('Lesson updated successfully');
      setEditingLesson(null);
      setEditingWorkshop(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to update lesson');
    }
  };

  // AI rewrite of the content currently in the editor. Nothing is saved
  // until the admin clicks Save — Cancel discards the AI version.
  const handleImproveWithAI = async () => {
    if (improvingLesson) return;
    if (!editForm.content || !editForm.content.trim()) {
      toast.error('Nothing to improve — the lesson is empty');
      return;
    }
    soundEffects.playClick();
    setImprovingLesson(true);
    const toastId = toast.loading('AI is rewriting this lesson…');
    try {
      const data = await AdminService.aiImproveContent(editForm.title, editForm.content);
      setEditForm({
        ...editForm,
        title: data.draft.title || editForm.title,
        content: markdownToHtml(data.draft.content)
      });
      toast.success('Improved version loaded — review it, then Save (or Cancel to discard)', { id: toastId });
    } catch (error) {
      toast.error(error.message || 'Failed to improve lesson', { id: toastId });
    } finally {
      setImprovingLesson(false);
    }
  };

  // Create handlers
  const handleCreateCourse = async () => {
    try {
      soundEffects.playClick();
      if (!courseForm.title) {
        toast.error('Course title is required');
        return;
      }
      
      const courseData = {
        ...courseForm,
        order: courseForm.order ? parseInt(courseForm.order) : undefined
      };
      
      await AdminService.createCourse(courseData);
      toast.success('Course created successfully');
      setShowCreateCourse(false);
      setCourseForm({
        title: '',
        description: '',
        thumbnail: '',
        color: '',
        order: '',
        isLocked: false
      });
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to create course');
    }
  };

  const handleCreateUnit = async () => {
    try {
      soundEffects.playClick();
      if (!unitForm.courseId || !unitForm.title) {
        toast.error('Course and unit title are required');
        return;
      }
      
      const unitData = {
        ...unitForm,
        courseId: parseInt(unitForm.courseId),
        order: unitForm.order ? parseInt(unitForm.order) : undefined
      };
      
      await AdminService.createUnit(unitData);
      toast.success('Unit created successfully');
      setShowCreateUnit(false);
      setUnitForm({
        courseId: '',
        title: '',
        description: '',
        order: '',
        isActive: true
      });
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to create unit');
    }
  };

  const handleCreateLesson = async () => {
    try {
      soundEffects.playClick();
      if (!lessonForm.unitId || !lessonForm.title || !lessonForm.content) {
        toast.error('Unit, title, and content are required');
        return;
      }
      
      const lessonData = {
        ...lessonForm,
        unitId: parseInt(lessonForm.unitId),
        order: lessonForm.order ? parseInt(lessonForm.order) : undefined,
        xpValue: parseInt(lessonForm.xpValue) || 50
      };
      
      await AdminService.createLesson(lessonData);
      toast.success('Lesson created successfully');
      setShowCreateLesson(false);
      setLessonForm({
        unitId: '',
        title: '',
        content: '',
        order: '',
        xpValue: 50,
        isActive: true
      });
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to create lesson');
    }
  };

  const handleCreateQuiz = async () => {
    try {
      soundEffects.playClick();
      if (!quizForm.lessonId || !quizForm.title) {
        toast.error('Lesson and quiz title are required');
        return;
      }
      
      const quizData = {
        ...quizForm,
        lessonId: parseInt(quizForm.lessonId),
        passingScore: parseInt(quizForm.passingScore) || 70,
        xpValue: parseInt(quizForm.xpValue) || 25,
        timeLimit: quizForm.timeLimit ? parseInt(quizForm.timeLimit) : null,
        questions: [] // Start with empty questions, can be edited later
      };
      
      await AdminService.createQuiz(quizData);
      toast.success('Quiz created successfully');
      setShowCreateQuiz(null);
      setQuizForm({
        lessonId: '',
        title: '',
        description: '',
        passingScore: 70,
        xpValue: 25,
        timeLimit: '',
        isActive: true
      });
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to create quiz');
    }
  };


  // Organize lessons by course and unit for hierarchical dropdown
  const organizedData = courses.map(course => {
    const courseLessons = lessons.filter(l => l.courseId === course.id);
    const unitsWithLessons = course.units.map(unit => {
      // Handle both unitId (from l.*) and unit_id (alias) for compatibility
      const unitLessons = courseLessons.filter(l => 
        (l.unitId === unit.id || l.unit_id === unit.id)
      );
      return { ...unit, lessons: unitLessons };
    });
    return { ...course, units: unitsWithLessons };
  });

  const filteredLessons = lessons.filter(lesson => {
    const matchesSearch = !searchTerm || 
      lesson.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lesson.content.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCourse = !selectedCourse || lesson.courseId === parseInt(selectedCourse);
    
    // Handle both unitId and unit_id for compatibility
    const lessonUnitId = lesson.unitId || lesson.unit_id;
    const matchesUnit = !selectedUnit || lessonUnitId === parseInt(selectedUnit);
    
    const matchesLesson = !selectedLesson || lesson.id === parseInt(selectedLesson);
    
    return matchesSearch && matchesCourse && matchesUnit && matchesLesson;
  });

  const getUnitsForCourse = (courseId) => {
    const course = courses.find(c => c.id === parseInt(courseId));
    return course ? course.units : [];
  };

  const getLessonsForUnit = (courseId, unitId) => {
    return lessons.filter(l => {
      const lessonUnitId = l.unitId || l.unit_id;
      return l.courseId === parseInt(courseId) && lessonUnitId === parseInt(unitId);
    });
  };

  const handleHierarchyChange = (value) => {
    if (!value) {
      // Reset all selections
      setSelectedCourse('');
      setSelectedUnit('');
      setSelectedLesson('');
      return;
    }

    const parts = value.split('-');
    if (parts[0] === 'course') {
      setSelectedCourse(parts[1]);
      setSelectedUnit('');
      setSelectedLesson('');
    } else if (parts[0] === 'unit') {
      setSelectedCourse(parts[1]);
      setSelectedUnit(parts[2]);
      setSelectedLesson('');
    } else if (parts[0] === 'lesson') {
      setSelectedCourse(parts[1]);
      setSelectedUnit(parts[2]);
      setSelectedLesson(parts[3]);
    }
  };

  const getSelectedHierarchyValue = () => {
    if (selectedLesson) {
      return `lesson-${selectedCourse}-${selectedUnit}-${selectedLesson}`;
    } else if (selectedUnit) {
      return `unit-${selectedCourse}-${selectedUnit}`;
    } else if (selectedCourse) {
      return `course-${selectedCourse}`;
    }
    return '';
  };

  if (loading) {
    return <div className="loading">Loading lessons...</div>;
  }

  return (
    <div className="admin-lessons-view">
      <div className="lessons-header">
        <h2>
          <FaBook /> Lesson Management
        </h2>
        <div className="lessons-actions">
          <button
            className="btn-create"
            onClick={() => {
              soundEffects.playClick();
              setShowCreateCourse(true);
            }}
          >
            <FaPlus /> Create Course
          </button>
          <button
            className="btn-create"
            onClick={() => {
              soundEffects.playClick();
              setShowCreateUnit(true);
            }}
          >
            <FaPlus /> Create Unit
          </button>
          <button
            className="btn-create"
            onClick={() => {
              soundEffects.playClick();
              setShowCreateLesson(true);
            }}
          >
            <FaPlus /> Create Lesson
          </button>
          <button
            className="btn-create btn-create-pdf"
            onClick={() => {
              soundEffects.playClick();
              setShowPdfUploader(true);
            }}
          >
            <FaFilePdf /> Create from PDF
          </button>
        </div>
        <div className="lessons-filters">
          <div className="search-box">
            <FaSearch />
            <input
              type="text"
              placeholder="Search lessons..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="hierarchy-dropdown-container">
            <select
              className="hierarchy-dropdown"
              value={getSelectedHierarchyValue()}
              onChange={(e) => handleHierarchyChange(e.target.value)}
            >
              <option value="">All Lessons</option>
              {organizedData.map(course => (
                <React.Fragment key={course.id}>
                  <option value={`course-${course.id}`} className="course-option">
                    📚 {course.title}
                  </option>
                  {course.units.map(unit => (
                    <React.Fragment key={unit.id}>
                      <option value={`unit-${course.id}-${unit.id}`} className="unit-option">
                        &nbsp;&nbsp;📖 {unit.title}
                      </option>
                      {unit.lessons.map(lesson => (
                        <option 
                          key={lesson.id} 
                          value={`lesson-${course.id}-${unit.id}-${lesson.id}`}
                          className="lesson-option"
                        >
                          &nbsp;&nbsp;&nbsp;&nbsp;📄 {lesson.title}
                        </option>
                      ))}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="lessons-list">
        {filteredLessons.length === 0 ? (
          <div className="no-lessons">No lessons found</div>
        ) : (
          filteredLessons.map(lesson => (
            <div key={lesson.id} className="lesson-card">
              {editingLesson === lesson.id ? (
                <div className="lesson-edit-form">
                  <div className="form-group">
                    <label>Title</label>
                    <input
                      type="text"
                      value={editForm.title}
                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Content</label>
                    {editingWorkshop ? (
                      <>
                        <textarea
                          className="workshop-json-editor"
                          value={editForm.content}
                          onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                          rows={22}
                          spellCheck={false}
                        />
                        <div className="editor-help-text">
                          <strong>interactive workshop lesson:</strong> this content is JSON that powers the
                          interactive slide player. Edit carefully — Save validates the format first.
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="rich-text-editor-container">
                          <RichTextEditor
                            value={editForm.content}
                            onChange={(content) => setEditForm({ ...editForm, content })}
                            placeholder="Enter lesson content... Use Ctrl+B for bold, Ctrl+I for italic, Ctrl+U for underline"
                          />
                        </div>
                        <div className="editor-help-text">
                          <strong>Keyboard Shortcuts:</strong> Ctrl+B (Bold), Ctrl+I (Italic), Ctrl+U (Underline)
                          <br />
                          <strong>Tip:</strong> Click the image icon in the toolbar to insert images directly into your content
                        </div>
                      </>
                    )}
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Order</label>
                      <input
                        type="number"
                        value={editForm.order}
                        onChange={(e) => setEditForm({ ...editForm, order: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>XP Value</label>
                      <input
                        type="number"
                        value={editForm.xpValue}
                        onChange={(e) => setEditForm({ ...editForm, xpValue: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={editForm.isActive}
                          onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                        />
                        Active
                      </label>
                    </div>
                  </div>
                  
                  <div className="form-actions">
                    <button
                      className="btn-save"
                      onClick={handleSave}
                    >
                      <FaSave /> Save
                    </button>
                    {!editingWorkshop && (
                      <button
                        className="btn-improve-ai"
                        onClick={handleImproveWithAI}
                        disabled={improvingLesson}
                        title="Rewrite this lesson with AI — review before saving"
                      >
                        {improvingLesson ? <FaSyncAlt className="spin" /> : <FaMagic />}
                        {improvingLesson ? ' Improving…' : ' Improve with AI'}
                      </button>
                    )}
                    <button
                      className="btn-cancel"
                      onClick={handleCancelEdit}
                    >
                      <FaTimes /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="lesson-header">
                    <div className="lesson-info">
                      <h3>{lesson.title}</h3>
                      <div className="lesson-meta">
                        <span className="course-badge">{lesson.course_title}</span>
                        <span className="unit-badge">{lesson.unit_title}</span>
                        <span className={`status-badge ${lesson.isActive ? 'active' : 'inactive'}`}>
                          {lesson.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    <div className="lesson-actions">
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(lesson)}
                      >
                        <FaEdit /> Edit
                      </button>
                      <button
                        className="btn-create-quiz"
                        onClick={() => {
                          soundEffects.playClick();
                          setQuizForm({
                            ...quizForm,
                            lessonId: lesson.id.toString()
                          });
                          setShowCreateQuiz(lesson.id);
                        }}
                        title="Create Quiz for this Lesson"
                      >
                        <FaClipboardCheck /> Create Quiz
                      </button>
                    </div>
                  </div>
                  
                  <div className="lesson-content-preview">
                    {lesson.content ? (
                      <div 
                        dangerouslySetInnerHTML={{ 
                          __html: lesson.content.length > 200 
                            ? lesson.content.substring(0, 200) + '...' 
                            : lesson.content 
                        }}
                      />
                    ) : (
                      <span className="no-content">No content</span>
                    )}
                  </div>
                  
                  <div className="lesson-footer">
                    <span>Order: {lesson.order}</span>
                    <span>XP: {lesson.xpValue}</span>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>

      {/* Create Course Modal */}
      {showCreateCourse && (
        <div className="modal-overlay" onClick={() => setShowCreateCourse(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Course</h3>
              <button className="modal-close" onClick={() => setShowCreateCourse(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={courseForm.title}
                  onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
                  placeholder="Course title"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={courseForm.description}
                  onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                  placeholder="Course description"
                  rows="3"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Thumbnail (emoji)</label>
                  <input
                    type="text"
                    value={courseForm.thumbnail}
                    onChange={(e) => setCourseForm({ ...courseForm, thumbnail: e.target.value })}
                    placeholder="e.g. 📚"
                  />
                </div>
                <div className="form-group">
                  <label>Color</label>
                  <input
                    type="text"
                    value={courseForm.color}
                    onChange={(e) => setCourseForm({ ...courseForm, color: e.target.value })}
                    placeholder="#000000"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Order</label>
                  <input
                    type="number"
                    value={courseForm.order}
                    onChange={(e) => setCourseForm({ ...courseForm, order: e.target.value })}
                    placeholder="Auto"
                  />
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={courseForm.isLocked}
                      onChange={(e) => setCourseForm({ ...courseForm, isLocked: e.target.checked })}
                    />
                    Locked
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowCreateCourse(false)}>
                <FaTimes /> Cancel
              </button>
              <button className="btn-save" onClick={handleCreateCourse}>
                <FaSave /> Create Course
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Unit Modal */}
      {showCreateUnit && (
        <div className="modal-overlay" onClick={() => setShowCreateUnit(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Unit</h3>
              <button className="modal-close" onClick={() => setShowCreateUnit(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Course *</label>
                <select
                  value={unitForm.courseId}
                  onChange={(e) => setUnitForm({ ...unitForm, courseId: e.target.value })}
                >
                  <option value="">Select a course</option>
                  {courses.map(course => (
                    <option key={course.id} value={course.id}>{course.title}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={unitForm.title}
                  onChange={(e) => setUnitForm({ ...unitForm, title: e.target.value })}
                  placeholder="Unit title"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={unitForm.description}
                  onChange={(e) => setUnitForm({ ...unitForm, description: e.target.value })}
                  placeholder="Unit description"
                  rows="3"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Order</label>
                  <input
                    type="number"
                    value={unitForm.order}
                    onChange={(e) => setUnitForm({ ...unitForm, order: e.target.value })}
                    placeholder="Auto"
                  />
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={unitForm.isActive}
                      onChange={(e) => setUnitForm({ ...unitForm, isActive: e.target.checked })}
                    />
                    Active
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowCreateUnit(false)}>
                <FaTimes /> Cancel
              </button>
              <button className="btn-save" onClick={handleCreateUnit}>
                <FaSave /> Create Unit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Lesson Modal */}
      {showCreateLesson && (
        <div className="modal-overlay" onClick={() => setShowCreateLesson(false)}>
          <div className="modal-content modal-content-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Lesson</h3>
              <button className="modal-close" onClick={() => setShowCreateLesson(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Unit *</label>
                <select
                  value={lessonForm.unitId}
                  onChange={(e) => setLessonForm({ ...lessonForm, unitId: e.target.value })}
                >
                  <option value="">Select a unit</option>
                  {courses.map(course => 
                    course.units.map(unit => (
                      <option key={unit.id} value={unit.id}>
                        {course.title} - {unit.title}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={lessonForm.title}
                  onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })}
                  placeholder="Lesson title"
                />
              </div>
              <div className="form-group">
                <label>Content *</label>
                <div className="rich-text-editor-container">
                  <RichTextEditor
                    value={lessonForm.content}
                    onChange={(content) => setLessonForm({ ...lessonForm, content })}
                    placeholder="Enter lesson content..."
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Order</label>
                  <input
                    type="number"
                    value={lessonForm.order}
                    onChange={(e) => setLessonForm({ ...lessonForm, order: e.target.value })}
                    placeholder="Auto"
                  />
                </div>
                <div className="form-group">
                  <label>XP Value</label>
                  <input
                    type="number"
                    value={lessonForm.xpValue}
                    onChange={(e) => setLessonForm({ ...lessonForm, xpValue: parseInt(e.target.value) || 50 })}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={lessonForm.isActive}
                      onChange={(e) => setLessonForm({ ...lessonForm, isActive: e.target.checked })}
                    />
                    Active
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowCreateLesson(false)}>
                <FaTimes /> Cancel
              </button>
              <button className="btn-save" onClick={handleCreateLesson}>
                <FaSave /> Create Lesson
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Quiz Modal */}
      {showCreateQuiz && (
        <div className="modal-overlay" onClick={() => setShowCreateQuiz(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Quiz</h3>
              <button className="modal-close" onClick={() => setShowCreateQuiz(null)}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Lesson *</label>
                <select
                  value={quizForm.lessonId}
                  onChange={(e) => setQuizForm({ ...quizForm, lessonId: e.target.value })}
                >
                  <option value="">Select a lesson</option>
                  {lessons.map(lesson => (
                    <option key={lesson.id} value={lesson.id}>
                      {lesson.course_title} - {lesson.unit_title} - {lesson.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={quizForm.title}
                  onChange={(e) => setQuizForm({ ...quizForm, title: e.target.value })}
                  placeholder="Quiz title"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={quizForm.description}
                  onChange={(e) => setQuizForm({ ...quizForm, description: e.target.value })}
                  placeholder="Quiz description"
                  rows="3"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Passing Score (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={quizForm.passingScore}
                    onChange={(e) => setQuizForm({ ...quizForm, passingScore: parseInt(e.target.value) || 70 })}
                  />
                </div>
                <div className="form-group">
                  <label>XP Value</label>
                  <input
                    type="number"
                    value={quizForm.xpValue}
                    onChange={(e) => setQuizForm({ ...quizForm, xpValue: parseInt(e.target.value) || 25 })}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Time Limit (minutes)</label>
                  <input
                    type="number"
                    value={quizForm.timeLimit}
                    onChange={(e) => setQuizForm({ ...quizForm, timeLimit: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={quizForm.isActive}
                      onChange={(e) => setQuizForm({ ...quizForm, isActive: e.target.checked })}
                    />
                    Active
                  </label>
                </div>
              </div>
              <div className="info-box">
                <p><strong>Note:</strong> Questions can be added after creating the quiz. Go to the Quizzes tab to edit questions.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowCreateQuiz(null)}>
                <FaTimes /> Cancel
              </button>
              <button className="btn-save" onClick={handleCreateQuiz}>
                <FaSave /> Create Quiz
              </button>
            </div>
          </div>
        </div>
      )}
      {/* PDF Lesson Uploader Modal */}
      {showPdfUploader && (
        <div className="modal-overlay" onClick={() => setShowPdfUploader(false)}>
          <div className="modal-content modal-content-large" onClick={(e) => e.stopPropagation()}>
            <PdfLessonUploader
              unitId={selectedUnitForPdf}
              availableUnits={courses.flatMap(course => 
                course.units.map(unit => ({
                  ...unit,
                  courseTitle: course.title
                }))
              )}
              onLessonCreated={(lesson) => {
                toast.success('Lesson created from PDF successfully!');
                setShowPdfUploader(false);
                setSelectedUnitForPdf(null);
                loadData();
              }}
              onClose={() => {
                setShowPdfUploader(false);
                setSelectedUnitForPdf(null);
              }}
            />
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminLessonsView;

